/**
 * ============================================================================
 * DEMO CONFIGURATION - Single Source of Truth
 * ============================================================================
 * 
 * Change demo settings here - everything else updates automatically!
 * 
 * To switch scenarios in demo mode:
 * 1. Change DEMO_SCENARIO_ID below
 * 2. Rebuild: npm run build
 * 3. Deploy: gcloud app deploy
 * 
 * That's it! ✅
 */

// ============================================================================
// PRIMARY DEMO SETTINGS (Change these!)
// ============================================================================

/**
 * Which scenario to show in demo mode
 * 
 * Options (workspace_id = 0):
 * - 1: "Current Baseline" - Existing brownfield network (before optimization)
 * - 2: "Optimized Baseline" - Optimized network (shows cost savings!)
 * - 3: "High Service (99%)" - 99% service level constraint
 */
export const DEMO_SCENARIO_ID = 2  // ⭐ CHANGE THIS to switch scenarios!

/**
 * Scenario display name (shown in UI)
 */
export const DEMO_SCENARIO_NAME = 'Optimized Baseline'  // Update when changing ID

/**
 * Demo workspace ID (always 0 for demo data)
 */
export const DEMO_WORKSPACE_ID = 0

// ============================================================================
// STATS BAR CONFIGURATION (Easy to Customize!)
// ============================================================================

/**
 * Order of statistics cards in the summary bar (left to right)
 * 
 * Simply reorder this array to change the display order!
 * Add/remove cards as needed.
 * 
 * Available cards:
 * - totalCost: Total network cost
 * - transportCost: Shipping/transport cost
 * - fixedCost: Fixed facility costs
 * - variableCost: Variable operating costs
 * - totalDemand: Total demand volume
 * - serviceLevel: Service level percentage
 * - capacityUtil: Capacity utilization
 * - networkRisk: Network risk score (1-5)
 * - dataQuality: Data quality score (0-100%)
 * - totalFacilities: Number of facilities
 * - totalCustomers: Number of customers
 * - spareCapacity: Spare capacity available
 */
export const STATS_CARD_ORDER = [
  'totalCost',       // 1st: Total Cost
  'transportCost',   // 2nd: Transport Cost
  'fixedCost',       // 3rd: Fixed Cost
  'variableCost',    // 4th: Variable Cost
  'totalDemand',     // 5th: Demand
  'serviceLevel',    // 6th: Service Level
  'capacityUtil',    // 7th: Capacity Utilization
  'valueOfStochasticSolution',  // 8th: Cost of Averaging
]

/**
 * Which cards are enabled by default
 * Set to false to hide a card
 */
export const STATS_DEFAULT_ACTIVE_CARDS = {
  // Shown, in STATS_CARD_ORDER above.
  totalCost: true,
  transportCost: true,
  fixedCost: true,
  variableCost: true,
  totalDemand: true,
  serviceLevel: true,
  capacityUtil: true,

  // Hidden. EVERY card must be listed, including the off ones: activeCardsArray
  // treats an absent key as visible
  //     configuredValue === undefined ? (defaultValue !== false) : ...
  // so an omitted card is shown, not hidden. holdingCost was missing and
  // appeared in the bar for exactly that reason.
  holdingCost: false,
  networkRisk: false,
  dataQuality: false,
  demandFulfilled: false,
  openDCs: false,
  totalFacilities: false,
  totalCustomers: false,
  spareCapacity: false,
  baselineDelta: false,

  // Shown, and deliberately shown empty. Until a design-under-uncertainty run
  // exists it renders "—" with the subtitle "Not yet run", which advertises the
  // capability without asserting a result. The alternative — hide it until a
  // result appears — means only people who already know the feature exists ever
  // find it, and no production scenario has a stored result today, so the card
  // would be invisible to everyone.
  //
  // What it must never do is render "$0" while empty. Zero is a real answer
  // there (the average-demand footprint happened to be the robust one); an
  // absent run is not. getMainValue enforces the distinction, cardHelpers.spec.js
  // holds it in place.
  valueOfStochasticSolution: true
}

// ============================================================================
// PRESENTATION MODE CONFIGURATION (Demo Mode ONLY!)
// ============================================================================

/**
 * Auto-enable presentation mode in DEMO MODE ONLY
 * 
 * IMPORTANT: This ONLY applies when accessing via ?demo=true URL parameter
 * 
 * Does NOT apply to:
 * - Free trial users (logged in)
 * - Essential tier (paid)
 * - Professional tier (paid)
 * - Enterprise tier (paid)
 * 
 * Those users control presentation mode themselves via Menu → Presentation Mode
 */
export const DEMO_PRESENTATION_MODE_ENABLED = true

/**
 * Which legends/overlays to show in DEMO MODE ONLY
 * (Free trial and paid users configure this themselves)
 */
export const DEMO_PRESENTATION_SETTINGS = {
  legends: {
    showHeatmap: true,           // ✅ Service Level Legend (% of customer demand)
    showMapLegend: true,          // ✅ Facilities Legend (icon types)
    heatmapMode: 'service',       // 'service', 'utilization', or 'cost'
    heatmapPosition: 'Left Middle'
  },
  widgets: {
    showTop10Facilities: false,   // Top 10 facilities widget (off for cleaner demo)
    showTop10Lanes: false          // Top 10 lanes widget (off for cleaner demo)
  },
  display: {
    cleanView: true,              // Clean, professional look
    highContrast: true,           // Better visibility
    showBigNumbers: true          // Large readable numbers
  }
}

// ============================================================================
// SCENARIO METADATA (Reference)
// ============================================================================

/**
 * Available scenarios in demo workspace
 * Use this as reference when changing DEMO_SCENARIO_ID
 */
export const AVAILABLE_SCENARIOS = {
  1: {
    id: 1,
    name: 'Current Baseline',
    description: 'Existing brownfield network without optimization',
    type: 'baseline',
    showsOptimization: false,
    keyFeatures: [
      'Shows current network state',
      'No optimization applied',
      'Good for comparison baseline'
    ]
  },
  2: {
    id: 2,
    name: 'Optimized Baseline',
    description: 'Optimized version of current baseline network',
    type: 'optimized',
    showsOptimization: true,
    keyFeatures: [
      'Shows optimized network',
      'Demonstrates cost savings',
      'Best for sales demos!' // ⭐ Recommended
    ]
  },
  3: {
    id: 3,
    name: 'High Service (99%)',
    description: 'High service level target of 99%',
    type: 'optimized',
    showsOptimization: true,
    keyFeatures: [
      '99% service level guarantee',
      'Shows service level optimization',
      'Good for service-focused customers'
    ]
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get current demo scenario metadata
 */
export function getDemoScenarioInfo() {
  return AVAILABLE_SCENARIOS[DEMO_SCENARIO_ID] || AVAILABLE_SCENARIOS[1]
}

/**
 * Check if demo is showing an optimized scenario
 */
export function isDemoShowingOptimization() {
  const scenario = getDemoScenarioInfo()
  return scenario.showsOptimization
}

/**
 * Get demo scenario display name
 */
export function getDemoScenarioDisplayName() {
  return DEMO_SCENARIO_NAME || getDemoScenarioInfo().name
}

// ============================================================================
// USAGE EXAMPLES
// ============================================================================

/*
// In any Vue component or store:
import { DEMO_SCENARIO_ID, DEMO_WORKSPACE_ID, getDemoScenarioInfo } from '@/config/demoConfig'

// Use in API calls:
const data = await api.getScenarioData(DEMO_SCENARIO_ID)

// Get scenario metadata:
const scenarioInfo = getDemoScenarioInfo()
console.log(`Demo showing: ${scenarioInfo.name}`)
console.log(`Features: ${scenarioInfo.keyFeatures.join(', ')}`)

// Check if showing optimization:
if (isDemoShowingOptimization()) {
  console.log('Demo shows optimization results!')
}
*/

// ============================================================================
// QUICK REFERENCE GUIDE
// ============================================================================

/*
┌─────────────────────────────────────────────────────────────────┐
│ SCENARIO COMPARISON TABLE                                        │
├──────────┬─────────────────────┬────────────┬───────────────────┤
│ ID       │ Name                │ Type       │ Best For          │
├──────────┼─────────────────────┼────────────┼───────────────────┤
│ 1        │ Current Baseline    │ Baseline   │ Showing "before"  │
│ 2 ⭐     │ Optimized Baseline  │ Optimized  │ Sales demos!      │
│ 3        │ High Service (99%)  │ Optimized  │ Service focus     │
└──────────┴─────────────────────┴────────────┴───────────────────┘

⭐ Recommended: Scenario 2 (Optimized Baseline)
   - Shows clear cost savings
   - Demonstrates optimization value
   - Best first impression for prospects

TO SWITCH SCENARIOS:
1. Change line 28: export const DEMO_SCENARIO_ID = 2
2. Change line 33: export const DEMO_SCENARIO_NAME = 'Your Name'
3. Rebuild: npm run build
4. Deploy: gcloud app deploy
5. Done! ✅

Note: Demo data is cached in demoData.js for fast loading.
      Switching scenarios via config only changes which scenario
      the NON-cached (full app) mode loads. For fully cached demo,
      you'd need to also update demoData.js with scenario 2's data.
*/





